import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Database, Upload, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import ClearPathLogo from '../components/ClearPathLogo';
import { LoadingLogo } from '../components/LoadingScreen';
import { toast } from 'sonner';
import { fmt, fmtPct } from '../utils/formatters';
import { PRO_PRICE_LABEL, FOUNDING_PRICE_LABEL } from '../pricing';
import { useCountUp } from '../components/TimeMachineRevealModal';
import { pageCache } from '../utils/pageCache';
import { describeUpload } from '../utils/uploadSummary';
import UploadKindDialog from '../components/UploadKindDialog';
import { InspectResult, UploadKind, importedLine } from '../utils/uploadKind';

/* ---------------------------------------------------------------------------
 * Three questions, one action, one reveal.
 *
 * Two fields the old flow REQUIRED are gone: `state` and `monthly_revenue_range`
 * are read by no logic anywhere in the codebase, yet they gated the Continue
 * button. `cogs_percentage` stays because the engine genuinely reads it — it
 * feeds the Time Machine backtest and the snapshot's cost imputation.
 * ------------------------------------------------------------------------- */

const BUSINESS_TYPES = ['Coffee Shop', 'Restaurant', 'Retail Store', 'Bakery', 'Food Truck', 'Bar', 'Other'];
const BENCHMARKS: Record<string, number> = {
  'Coffee Shop': 30, 'Restaurant': 35, 'Retail Store': 45,
  'Bakery': 32, 'Food Truck': 28, 'Bar': 25, 'Other': 30,
};
// Shown under the cost question so the number has a reference point.
const BENCHMARK_RANGE: Record<string, string> = {
  'Coffee Shop': '28–32%', 'Restaurant': '32–38%', 'Retail Store': '42–48%',
  'Bakery': '30–34%', 'Food Truck': '26–30%', 'Bar': '22–28%', 'Other': '28–34%',
};

// "Food cost" is the trade term in a kitchen and nonsense in a shop that sells
// candles. The question renames itself rather than asking a retailer about food.
const FOOD_BUSINESSES = new Set(['Coffee Shop', 'Restaurant', 'Bakery', 'Food Truck', 'Bar']);
const costNoun = (type: string) => (FOOD_BUSINESSES.has(type) ? 'food cost' : 'cost of goods');

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// framer-motion 11 types AnimatePresence's return as `Element | undefined`, which
// React 18's JSX namespace rejects (it wants `Element | null`). The component
// works; only the declaration disagrees. Narrowed here, once, rather than
// sprinkling `any` at the call site or dropping the exit transition.
const Presence = AnimatePresence as unknown as React.FC<{
  mode?: 'wait' | 'sync' | 'popLayout';
  children?: React.ReactNode;
}>;

interface RevealContext {
  days_of_data: number;
  txn_count: number;
  min_days: number;
  min_txns: number;
  sufficient: boolean;
  brief_hour: number;
  timezone: string;
  stats: {
    best_seller: { name: string; rev_30d: number } | null;
    busiest_hour: { hour: number; rev_share: number } | null;
    quietest_day: { weekday: number } | null;
    thinnest_margin: { name: string; margin: number } | null;
  };
}

const hourLabel = (h: number) => {
  const to12 = (n: number) => `${n % 12 || 12}`;
  const ampm = h < 12 ? 'am' : 'pm';
  return `${to12(h)}–${to12(h + 1)}${ampm}`;
};

/**
 * When the first brief actually arrives — or, honestly, why it will not.
 *
 * The runner skips a user entirely unless days_of_data >= 28 AND
 * txn_count >= 200, so a hardcoded "tomorrow at 7am" would be silently false
 * for most new accounts and they would wait for an email that never comes.
 * Days takes precedence when both fall short: it is the constraint that
 * governs waiting.
 */
export const nextBriefLine = (ctx: RevealContext): string => {
  if (ctx.sufficient) {
    const h = ctx.brief_hour % 12 || 12;
    const ampm = ctx.brief_hour < 12 ? 'am' : 'pm';
    return `Your first brief arrives tomorrow at ${h}:00${ampm}.`;
  }
  if (ctx.days_of_data < ctx.min_days) {
    const left = ctx.min_days - ctx.days_of_data;
    return `You've given us ${ctx.days_of_data} days of sales. Briefs start at ${ctx.min_days} — about ${left} ${left === 1 ? 'day' : 'days'} from now, or straight away if you upload more history.`;
  }
  return `You've given us ${ctx.days_of_data} days and ${ctx.txn_count} sales. Briefs start at ${ctx.min_txns} sales, so we're nearly there.`;
};

export default function OnboardingPage() {
  const [screen, setScreen] = useState(1);
  const [businessName, setBusinessName] = useState('');
  // Optional: where the shop is, for weather. Placed by the Census geocoder.
  const [address, setAddress] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [cogsPercentage, setCogsPercentage] = useState(30);
  const [busy, setBusy] = useState<string | null>(null);
  const [revealState, setRevealState] = useState<'idle' | 'running' | 'done'>('idle');
  const [report, setReport] = useState<any>(null);
  // A code from a pitch card. Optional, never called a "discount code" on
  // any surface — it is just a code the owner was handed.
  const [code, setCode] = useState('');
  const [codeState, setCodeState] = useState<'idle' | 'checking' | 'good' | 'bad'>('idle');
  const [shopifyShop, setShopifyShop] = useState('');
  const [askShopify, setAskShopify] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [ctx, setCtx] = useState<RevealContext | null>(null);
  const [pendingCsv, setPendingCsv] = useState<{ file: File; inspect: InspectResult } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const upgradeRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const reduce = useReducedMotion();

  const isPro = user?.plan === 'pro';

  const saveAnswers = useCallback(() => axios.post('/api/onboarding/complete', {
    business_name: businessName,
    business_type: businessType,
    cogs_percentage: cogsPercentage,
    // Never asked — the browser already knows. Without it the scheduler
    // assumes America/New_York and a Denver shop's 7am brief lands at 5am.
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    // Stored for later, not spent here. A bad one is ignored server-side so
    // a typo can never trap someone inside onboarding.
    code: codeState === 'good' ? code.trim() : undefined,
    address: address.trim() || undefined,
  }), [businessName, businessType, cogsPercentage, code, codeState, address]);

  const [codeIsFounding, setCodeIsFounding] = useState(false);

  const checkCode = useCallback(async () => {
    const value = code.trim();
    if (!value) return;
    setCodeState('checking');
    try {
      const res = await axios.post('/api/onboarding/check-code', { code: value });
      setCodeIsFounding(res.data?.type === 'founding_rate');
      setCodeState('good');
    } catch {
      setCodeIsFounding(false);
      setCodeState('bad');
    }
  }, [code]);

  // The Time Machine reveal, plus the context that decides the closing line.
  // `prefetched`: the sample load returns its report with the response, so
  // the reveal shows it at once instead of replaying the whole history again.
  const runReveal = useCallback(async (prefetched?: any) => {
    setRevealState('running');
    const fetchCtx = async () => {
      try {
        const r = await axios.get('/api/onboarding/reveal-context');
        setCtx(r.data);
      } catch { /* the reveal still stands without the closing line */ }
    };

    let rpt: any = prefetched ?? null;
    if (!(rpt?.total_found_point > 0)) {
      try {
        rpt = (await axios.post('/api/timemachine/run')).data?.report;
      } catch { /* fall through to polling */ }
    }

    // Poll whether or not the POST threw. The old version only polled inside
    // the catch, so a POST that returned no report skipped straight to /home.
    if (!(rpt?.total_found_point > 0)) {
      for (let i = 0; i < 10 && !(rpt?.total_found_point > 0); i++) {
        await new Promise((r) => setTimeout(r, 2000));
        try { rpt = (await axios.get('/api/timemachine')).data?.report; } catch { /* keep polling */ }
      }
    }
    await fetchCtx();
    if (rpt?.total_found_point > 0) { setReport(rpt); setRevealState('done'); return; }
    navigate('/home'); // nothing worth revealing — don't manufacture a moment
  }, [navigate]);

  // Returning from a POS OAuth round trip. The callback sends us to
  // /onboarding?connected=<provider> because the target rode the state record.
  // Back from Stripe. refreshUser() so isPro flips and the Pro badges vanish;
  // stay on screen 4 so the next click is the register they came for.
  useEffect(() => {
    const payment = new URLSearchParams(window.location.search).get('payment');
    if (payment) {
      setScreen(4);
      window.history.replaceState({}, '', '/onboarding');
      if (payment === 'success') {
        refreshUser().then(() => toast.success("You're on Pro. Now connect your register."));
      }
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('connected')) {
      setScreen(5);
      refreshUser().finally(runReveal);
    }
    // Mount only, deliberately: this reads the URL the OAuth callback sent us
    // to. Re-running it on identity changes would restart the reveal.
  }, []); // eslint-disable-line

  useEffect(() => {
    if (user?.onboarding_completed && revealState === 'idle'
        && !new URLSearchParams(window.location.search).get('connected')) {
      navigate('/home', { replace: true });
    }
  }, [user, navigate, revealState]);

  // Clicking a Pro-gated button opens the upgrade panel rather than leaving for
  // Stripe immediately. Leaving immediately meant an owner holding a code never
  // saw the field — it was further down the page than the button they clicked.
  const gate = () => {
    setShowUpgrade(true);
    // The panel renders below the buttons; on a phone it is off-screen.
    setTimeout(() => upgradeRef.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' }), 50);
  };

  const upgrade = async () => {
    setBusy('upgrade');
    try {
      await saveAnswers();
      const applied = codeState === 'good' ? code.trim() : '';
      const endpoint = applied ? '/api/stripe/create-checkout-with-discount' : '/api/stripe/create-checkout';
      const res = await axios.post(endpoint, {
        ...(applied ? { discount_code: applied } : {}),
        return_to: 'onboarding',
      });
      window.location.href = res.data.url;
    } catch {
      setBusy(null);
      toast.error("Couldn't start checkout. Check your connection and try again.");
    }
  };

  const connectShopify = async () => {
    const shop = shopifyShop.trim();
    if (!shop) { toast.error('Enter your store address first'); return; }
    setBusy('shopify');
    try {
      await saveAnswers();
      window.location.href = `/api/pos/shopify/connect?shop=${encodeURIComponent(shop)}&return_to=onboarding`;
    } catch {
      setBusy(null);
      toast.error("Couldn't save your answers, so we didn't send you to Shopify.");
    }
  };

  const connectPos = async (provider: 'square' | 'clover') => {
    setBusy(provider);
    try {
      // Persist BEFORE leaving the app. Without this the OAuth round trip
      // destroys every answer and drops them back on screen 1.
      await saveAnswers();
      window.location.href = `/api/pos/${provider}/connect?return_to=onboarding`;
    } catch {
      setBusy(null);
      toast.error("Couldn't save your answers, so we didn't send you to " +
        `${provider === 'square' ? 'Square' : 'Clover'}. Check your connection and try again.`);
    }
  };

  // Inspect first, import only after the owner confirms what the file is
  // (spec 2026-09-24-three-streams). Register sales go on to the reveal;
  // payments and bills go to their own sections and never touch sales.
  const uploadCsv = async (file: File) => {
    setBusy('csv');
    try {
      await saveAnswers();
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post('/api/upload/inspect', fd);
      setPendingCsv({ file, inspect: res.data });
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "That file couldn't be read. Export it as CSV and try again.");
    } finally {
      setBusy(null);
    }
  };

  const confirmCsv = async (kind: UploadKind) => {
    if (!pendingCsv) return;
    setBusy('csv');
    try {
      const fd = new FormData();
      fd.append('file', pendingCsv.file);
      fd.append('kind', kind);
      const res = await axios.post('/api/upload', fd);
      setPendingCsv(null);
      pageCache.clear('/api/');
      await refreshUser();
      if (kind !== 'sales') {
        toast.success(importedLine(kind, res.data.records));
        navigate(kind === 'payments' ? '/payments' : '/home');
        return;
      }
      // Say what was read before the reveal, so the number that follows is
      // visibly about this file.
      const { headline, detail, notice } = describeUpload(res.data);
      toast.success(headline, detail ? { description: detail } : undefined);
      if (notice) {
        // Not item sales: say so plainly and skip a reveal that has nothing
        // honest to show.
        toast.warning(notice, { duration: 12000 });
        navigate('/home');
        return;
      }
      setScreen(5);
      await runReveal();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "That file couldn't be read. Export it as CSV and try again.");
    } finally {
      setBusy(null);
    }
  };

  const useSample = async () => {
    setBusy('sample');
    try {
      await saveAnswers();
      const res = await axios.post('/api/load-sample-data');
      pageCache.clear('/api/');
      await refreshUser();
      setScreen(5);
      await runReveal(res.data?.report);
    } catch (e: any) {
      // Surface the server's reason (e.g. the 409 "you already have…" guard)
      // instead of blaming the connection — the generic line sent people
      // into retry loops when the real cause was on the account.
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Couldn't load the sample data. Check your connection and try again.");
    } finally {
      setBusy(null);
    }
  };

  // Arrow keys move between questions — this gets walked through out loud, and
  // reaching for the trackpad mid-sentence breaks the thread. Guarded so typing
  // a shop name or nudging the cost field never navigates.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (revealState !== 'idle') return;
      const el = document.activeElement;
      if (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      if (e.key === 'ArrowRight' && screen < 4 && !(screen === 1 && !businessName.trim())
          && !(screen === 2 && !businessType)) setScreen((n) => n + 1);
      if (e.key === 'ArrowLeft' && screen > 1) setScreen((n) => n - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [screen, revealState, businessName, businessType]);

  const found = report?.total_found_point ?? null;
  const counted = useCountUp(revealState === 'done' ? found : null, !reduce);

  const variants = reduce
    ? { initial: {}, animate: {}, exit: {} }
    : { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -12 } };

  /* ----------------------------- the reveal ----------------------------- */
  if (revealState !== 'idle') {
    const stats = ctx?.stats;
    const rows: Array<[string, string]> = [];
    if (stats?.best_seller) rows.push(['Best seller', stats.best_seller.name]);
    if (stats?.busiest_hour) rows.push(['Busiest hour', `${hourLabel(stats.busiest_hour.hour)} · ${fmtPct(stats.busiest_hour.rev_share * 100)} of revenue`]);
    if (stats?.quietest_day) rows.push(['Quietest day', WEEKDAYS[stats.quietest_day.weekday] ?? '—']);
    if (stats?.thinnest_margin) rows.push(['Thinnest margin', `${stats.thinnest_margin.name} · ${fmtPct(stats.thinnest_margin.margin * 100)}`]);

    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-6">
        <div className="w-full max-w-lg">
          <div className="flex justify-center"><ClearPathLogo size="md" /></div>

          {revealState === 'running' ? (
            <div className="mt-10 bg-[#4A2D5E] rounded-[14px] px-8 py-14 text-center">
              <div className="mx-auto w-fit"><LoadingLogo /></div>
              <h1 className="mt-6 font-serif text-2xl text-white">Reading your history…</h1>
              <p className="mt-2 font-sans text-sm text-white/70">
                Replaying every detector over every sale you&rsquo;ve recorded.
              </p>
            </div>
          ) : (
            <motion.div
              initial={reduce ? {} : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10"
            >
              <p className="font-sans text-sm text-[#6B6670] text-center">
                Had ClearPath been watching, it would have found you
              </p>
              <div className="mt-3 text-center font-serif text-[64px] leading-none tabular-nums text-[#2E6B4F]">
                {fmt(counted)}
              </div>
              <p className="mt-3 font-sans text-sm text-[#6B6670] text-center">
                {fmt(report.total_found_low)}–{fmt(report.total_found_high)} across{' '}
                {report.weeks_analyzed} weeks — estimated, from your own data.
              </p>

              {rows.length > 0 && (
                <div className="mt-10">
                  <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.14em] text-[#918B96]">
                    What we can already see
                  </p>
                  <dl className="mt-3 divide-y divide-[#E6E2DE] border-y border-[#E6E2DE]">
                    {rows.map(([k, v]) => (
                      <div key={k} className="flex items-baseline justify-between gap-6 py-3">
                        <dt className="font-sans text-sm text-[#6B6670]">{k}</dt>
                        <dd className="font-sans text-sm text-[#2D2A2E] text-right">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}

              {ctx && (
                <p className="mt-8 font-serif text-lg leading-snug text-[#2D2A2E]">
                  {nextBriefLine(ctx)}
                  {ctx.sufficient && (
                    <span className="block mt-1 font-sans text-sm text-[#6B6670]">
                      You don&rsquo;t need to come back here. We&rsquo;ll come to you.
                    </span>
                  )}
                </p>
              )}

              <button
                onClick={() => navigate('/home')}
                className="mt-8 w-full bg-[#4A2D5E] text-white hover:bg-[#3A2050] rounded-[8px] h-11 font-sans font-medium text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A2D5E]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
              >
                See today&rsquo;s brief
              </button>
            </motion.div>
          )}
        </div>
      </div>
    );
  }

  /* ---------------------------- the questions ---------------------------- */
  const canContinue = screen === 1 ? businessName.trim().length > 0 : true;
  const advance = () => setScreen((s) => s + 1);

  const primaryBtn = 'w-full bg-[#4A2D5E] text-white hover:bg-[#3D254D] disabled:opacity-40 disabled:hover:bg-[#4A2D5E] rounded-[8px] h-11 font-sans font-medium text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A2D5E]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-cream';
  const question = 'font-serif text-[32px] leading-tight text-[#2D2A2E]';
  // Every question carries one claim about the product beneath it. This flow is
  // walked through out loud with a merchant as often as it is done alone, and a
  // screen holding a lone input gives whoever is presenting nothing to say.
  const teach = 'mt-4 font-sans text-[15px] leading-relaxed text-[#6B6670] max-w-[46ch]';

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      {pendingCsv && (
        <UploadKindDialog
          fileName={pendingCsv.file.name}
          inspect={pendingCsv.inspect}
          busy={busy === 'csv'}
          onConfirm={confirmCsv}
          onCancel={() => setPendingCsv(null)}
        />
      )}
      {/* Progress: a rail that fills, not dots that count what's left. */}
      <div className="h-[2px] w-full bg-[#E6E2DE]" aria-hidden="true">
        <div
          className="h-full w-full bg-[#4A2D5E] origin-left transition-transform duration-300 ease-out"
          style={{ transform: `scaleX(${(screen - 1) / 4})` }}
        />
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-[520px]">
          <div className="flex justify-center mb-12"><ClearPathLogo size="md" /></div>

          <Presence mode="wait">
            <motion.div
              key={screen}
              variants={variants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            >
              {screen === 1 && (
                <div>
                  <h1 className={question}>What should we call your shop?</h1>
                  <p className={teach}>
                    Every morning ClearPath writes to you by name. It isn&rsquo;t a dashboard you
                    have to remember to open — it comes to you.
                  </p>
                  <input
                    autoFocus
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && canContinue) advance(); }}
                    placeholder="Morning Ritual Coffee"
                    aria-label="Business name"
                    className="mt-8 w-full bg-transparent border-0 border-b border-[#E6E2DE] focus:border-[#4A2D5E] font-sans text-lg text-[#2D2A2E] placeholder:text-[#8B8492] pb-3 outline-none transition-colors"
                  />
                  <input
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && canContinue) advance(); }}
                    placeholder="Street address (optional)"
                    aria-label="Street address (optional)"
                    autoComplete="street-address"
                    className="mt-6 w-full bg-transparent border-0 border-b border-[#E6E2DE] focus:border-[#4A2D5E] font-sans text-base text-[#2D2A2E] placeholder:text-[#8B8492] pb-3 outline-none transition-colors"
                  />
                  <button onClick={advance} disabled={!canContinue} className={`${primaryBtn} mt-10`}>
                    Continue
                  </button>
                </div>
              )}

              {screen === 2 && (
                <div>
                  <h1 className={question}>What kind of place is it?</h1>
                  <p className={teach}>
                    This sets where your numbers start. ClearPath begins from what&rsquo;s typical
                    for your kind of shop, then replaces every assumption with your real figures
                    as they come in.
                  </p>
                  {/* Selects, deliberately without advancing. Tap-to-advance is the
                      fastest thing here for someone alone, and the worst thing here when
                      this screen is being walked through out loud — it yanks the page
                      away mid-sentence. Pace belongs to whoever is talking. */}
                  <div className="mt-8 grid grid-cols-2 gap-2">
                    {BUSINESS_TYPES.map((t) => (
                      <button
                        key={t}
                        aria-pressed={businessType === t}
                        onClick={() => { setBusinessType(t); setCogsPercentage(BENCHMARKS[t] ?? 30); }}
                        className={`text-left border rounded-[10px] px-4 h-12 font-sans text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A2D5E]/40 ${
                          businessType === t
                            ? 'bg-[#F3EEF7] border-[#4A2D5E] text-[#2D2A2E] font-medium'
                            : 'bg-white border-[#E6E2DE] hover:border-[#4A2D5E] text-[#2D2A2E]'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  <button onClick={advance} disabled={!businessType} className={`${primaryBtn} mt-8`}>
                    Continue
                  </button>
                </div>
              )}

              {screen === 3 && (
                <div>
                  <h1 className={question}>What&rsquo;s your {costNoun(businessType)}?</h1>
                  <p className={teach}>
                    {FOOD_BUSINESSES.has(businessType)
                      ? 'What you pay for ingredients, as a share of what you charge.'
                      : 'What you pay for the things you sell, as a share of what you charge.'}
                    {' '}This is the one number everything else hangs off. Estimated today — and once
                    you start forwarding invoices, replaced with what you actually paid, per item,
                    per supplier.
                  </p>
                  <div className="mt-6 flex items-baseline gap-2">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={cogsPercentage}
                      onChange={(e) => setCogsPercentage(Math.max(0, Math.min(100, Number(e.target.value))))}
                      aria-label={`${costNoun(businessType)} percentage`}
                      className="w-24 bg-transparent border-0 border-b border-[#E6E2DE] focus:border-[#4A2D5E] font-serif text-[40px] tabular-nums text-[#2D2A2E] pb-2 outline-none transition-colors"
                    />
                    <span className="font-serif text-[40px] text-[#6B6670]">%</span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={cogsPercentage}
                    onChange={(e) => setCogsPercentage(Number(e.target.value))}
                    aria-label={`${costNoun(businessType)} percentage slider`}
                    className="mt-6 w-full accent-[#4A2D5E]"
                  />
                  <p className="mt-4 font-sans text-sm text-[#6B6670]">
                    Typical for {businessType ? `a ${businessType.toLowerCase()}` : 'a shop like yours'} is{' '}
                    {BENCHMARK_RANGE[businessType] ?? '28–34%'}. You can change this later.
                  </p>

                  <button onClick={advance} className={`${primaryBtn} mt-10`}>Continue</button>
                </div>
              )}

              {screen === 4 && (
                <div>
                  <h1 className={question}>Now let&rsquo;s see your numbers.</h1>
                  <p className={teach}>
                    Connect the register once. After that you never log in again — ClearPath
                    reads your sales every night and emails you what&rsquo;s worth doing.
                  </p>

                  <div className="mt-8 space-y-2">
                    {(['square', 'clover'] as const).map((p) => (
                      <button
                        key={p}
                        onClick={() => isPro ? connectPos(p) : gate()}
                        disabled={busy !== null}
                        className="w-full flex items-center gap-3 bg-white border border-[#E6E2DE] hover:border-[#4A2D5E] disabled:opacity-50 rounded-[10px] px-4 h-14 font-sans text-sm text-[#2D2A2E] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A2D5E]/40"
                      >
                        <Database size={18} strokeWidth={1.5} className="text-[#4A2D5E]" />
                        <span className="flex-1 text-left">
                          {busy === p ? `Taking you to ${p === 'square' ? 'Square' : 'Clover'}…`
                                      : `Connect ${p === 'square' ? 'Square' : 'Clover'}`}
                        </span>
                        {!isPro && <span className="font-sans text-[11px] uppercase tracking-[0.1em] text-[#918B96]">Pro</span>}
                      </button>
                    ))}

                    {/* Shopify needs the store address before we can send them
                        anywhere, so the row opens an input instead of leaving. */}
                    <button
                      onClick={() => isPro ? setAskShopify((v) => !v) : gate()}
                      disabled={busy !== null}
                      className="w-full flex items-center gap-3 bg-white border border-[#E6E2DE] hover:border-[#4A2D5E] disabled:opacity-50 rounded-[10px] px-4 h-14 font-sans text-sm text-[#2D2A2E] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A2D5E]/40"
                    >
                      <Database size={18} strokeWidth={1.5} className="text-[#4A2D5E]" />
                      <span className="flex-1 text-left">
                        {busy === 'shopify' ? 'Taking you to Shopify…' : 'Connect Shopify'}
                      </span>
                      {!isPro && <span className="font-sans text-[11px] uppercase tracking-[0.1em] text-[#918B96]">Pro</span>}
                    </button>
                    {askShopify && isPro && (
                      <div className="flex gap-2 pl-1">
                        <input
                          value={shopifyShop}
                          onChange={(e) => setShopifyShop(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') connectShopify(); }}
                          placeholder="your-store.myshopify.com"
                          autoComplete="off"
                          className="flex-1 border border-[#E6E2DE] focus:border-[#4A2D5E] rounded-[8px] h-11 px-3 font-sans text-sm outline-none focus:ring-2 focus:ring-[#4A2D5E]/20"
                        />
                        <button
                          onClick={connectShopify}
                          disabled={busy !== null || !shopifyShop.trim()}
                          className="px-4 bg-[#4A2D5E] text-white rounded-[8px] font-sans text-sm font-medium hover:bg-[#3D254D] disabled:opacity-50 transition-colors"
                        >
                          Go
                        </button>
                      </div>
                    )}

                    <button
                      onClick={() => isPro ? fileRef.current?.click() : gate()}
                      disabled={busy !== null}
                      className="w-full flex items-center gap-3 bg-white border border-[#E6E2DE] hover:border-[#4A2D5E] disabled:opacity-50 rounded-[10px] px-4 h-14 font-sans text-sm text-[#2D2A2E] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A2D5E]/40"
                    >
                      <Upload size={18} strokeWidth={1.5} className="text-[#4A2D5E]" />
                      <span className="flex-1 text-left">
                        {busy === 'csv' ? 'Reading your file…' : 'Upload a CSV'}
                      </span>
                      {!isPro && <span className="font-sans text-[11px] uppercase tracking-[0.1em] text-[#918B96]">Pro</span>}
                    </button>
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".csv,text/csv"
                      className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadCsv(f); e.target.value = ''; }}
                    />
                  </div>

                  {!isPro && (
                    <div
                      ref={upgradeRef}
                      className={`mt-6 rounded-[12px] border p-5 transition-colors ${
                        showUpgrade ? 'border-[#4A2D5E]/40 bg-[#4A2D5E]/[0.03]' : 'border-[#E6E2DE]'
                      }`}
                    >
                      <p className="font-sans text-sm text-[#2D2A2E]">
                        Connecting a register is part of Pro —{' '}
                        <span className="tabular-nums">
                          {codeState === 'good' && codeIsFounding ? FOUNDING_PRICE_LABEL : PRO_PRICE_LABEL}
                        </span>
                        /month, cancel whenever.
                      </p>

                      <label htmlFor="cp-code" className="block mt-4 font-sans text-sm text-[#2D2A2E]">
                        Were you given a code?
                      </label>
                      <p className="mt-1 font-sans text-xs text-[#6B6670]">
                        Optional. Enter it here and it applies at checkout.
                      </p>
                      <div className="mt-3 flex gap-2 max-w-sm">
                        <input
                          id="cp-code"
                          value={code}
                          onChange={(e) => { setCode(e.target.value.toUpperCase()); setCodeState('idle'); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); checkCode(); } }}
                          placeholder="Enter code"
                          autoComplete="off"
                          className={`flex-1 border rounded-[8px] h-10 px-3 font-sans text-sm outline-none focus:ring-2 focus:ring-[#4A2D5E]/20 tracking-wider ${
                            codeState === 'good' ? 'border-[#2E6B4F] bg-[#2E6B4F]/5'
                              : codeState === 'bad' ? 'border-[#B54A4A]' : 'border-[#E6E2DE] focus:border-[#4A2D5E]'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={checkCode}
                          disabled={codeState === 'checking' || !code.trim()}
                          className="px-4 bg-white border border-[#E6E2DE] hover:border-[#4A2D5E] text-[#2D2A2E] rounded-[8px] font-sans text-sm font-medium disabled:opacity-50 transition-colors"
                        >
                          {codeState === 'checking' ? '…' : 'Apply'}
                        </button>
                      </div>
                      {codeState === 'good' && (
                        <p className="mt-2 font-sans text-xs text-[#2E6B4F]">
                          Code applied{codeIsFounding ? ` — ${FOUNDING_PRICE_LABEL}/mo, locked for life.` : '.'}
                        </p>
                      )}
                      {codeState === 'bad' && (
                        <p className="mt-2 font-sans text-xs text-[#B54A4A]">
                          We don&rsquo;t recognize that code. You can continue without it.
                        </p>
                      )}

                      <button
                        onClick={upgrade}
                        disabled={busy !== null}
                        className={`${primaryBtn} mt-5 flex items-center justify-center gap-2`}
                      >
                        {busy === 'upgrade' ? 'Taking you to checkout…' : 'Continue to checkout'}
                        {busy !== 'upgrade' && <ArrowRight size={16} strokeWidth={1.5} />}
                      </button>
                      <p className="mt-3 font-sans text-xs text-[#6B6670]">
                        You&rsquo;ll come straight back here to connect your register.
                      </p>
                    </div>
                  )}

                  <button
                    onClick={useSample}
                    disabled={busy !== null}
                    className={`${primaryBtn} mt-6 flex items-center justify-center gap-2`}
                  >
                    {busy === 'sample' ? 'Loading a year of sales…' : 'Show me with sample data'}
                    {busy !== 'sample' && <ArrowRight size={16} strokeWidth={1.5} />}
                  </button>
                </div>
              )}
            </motion.div>
          </Presence>

          {screen > 1 && screen < 5 && (
            <button
              onClick={() => setScreen((s) => s - 1)}
              className="mt-8 font-sans text-sm text-[#6B6670] hover:text-[#2D2A2E] transition-colors focus-visible:outline-none focus-visible:underline"
            >
              Back
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
